import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { Dropdown } from 'primereact/dropdown';
import { getClients, createClient, updateClient, deleteClient } from '../api';
import { Client } from '../types/client';
import { useNotification } from '../contexts/NotificationContext';
import { formatDate } from '../utils';
import { quickbooksService } from '../services/quickbooks.service';

const ClientManagement: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
  });
  const { addNotification } = useNotification();
  const dt = useRef<DataTable<Client[]>>(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const data = await getClients();
      setClients(data);
    } catch (error) {
      addNotification('error', 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (client: Client | null = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name,
        contactPerson: client.contactPerson || '',
        email: client.email,
        phone: client.phone || '',
        address: client.address || '',
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
    setFormData({
      name: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      addNotification('error', 'Name is required');
      return;
    }
    if (!formData.email.trim()) {
      addNotification('error', 'Email is required');
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      addNotification('error', 'Please enter a valid email address');
      return;
    }

    try {
      if (editingClient) {
        const updatedClient = await updateClient(editingClient.id, formData);
        setClients((prevClients) => prevClients.map((client) => (client.id === updatedClient.id ? updatedClient : client)));
        addNotification('success', 'Client updated successfully');
      } else {
        const newClient = await createClient(formData);
        setClients((prevClients) => [...prevClients, newClient]);
        addNotification('success', 'Client created successfully');
      }
      handleCloseDialog();
    } catch (error) {
      addNotification('error', `Failed to ${editingClient ? 'update' : 'create'} client`);
    }
  };

  const handleDeleteClient = async (id: number) => {
    confirmDialog({
      message: 'Are you sure you want to delete this client?',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await deleteClient(id);
          setClients((prevClients) => prevClients.filter((client) => client.id !== id));
          addNotification('success', 'Client deleted successfully');
        } catch (error) {
          addNotification('error', 'Failed to delete client');
        }
      },
    });
  };

  // QuickBooks customer assignment
  const [qbCustomers, setQbCustomers] = useState<Array<{id: string; name: string; email: string | null; phone: string | null; address: string | null}>>([]);
  const [selectedQbCustomer, setSelectedQbCustomer] = useState<{id: string; name: string} | null>(null);
  const [qbDialogVisible, setQbDialogVisible] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loadingQbCustomers, setLoadingQbCustomers] = useState(false);

  const fetchQbCustomers = async () => {
    try {
      setLoadingQbCustomers(true);
      const customers = await quickbooksService.getCustomers();
      setQbCustomers(customers);
    } catch (error) {
      addNotification('error', 'Failed to fetch QuickBooks customers');
      console.error('Error fetching QuickBooks customers:', error);
    } finally {
      setLoadingQbCustomers(false);
    }
  };

  const handleOpenQbDialog = (client: Client) => {
    setSelectedClient(client);
    setSelectedQbCustomer(client.quickbooksCustomerId ? { id: client.quickbooksCustomerId, name: 'Current Assignment' } : null);
    fetchQbCustomers();
    setQbDialogVisible(true);
  };

  const handleAssignQbCustomer = async () => {
    if (!selectedClient || !selectedQbCustomer) return;
    
    try {
      const updatedClient = await updateClient(selectedClient.id, { quickbooksCustomerId: selectedQbCustomer.id });
      setClients((prevClients) => prevClients.map((client) => (client.id === updatedClient.id ? updatedClient : client)));
      addNotification('success', `QuickBooks customer "${selectedQbCustomer.name}" assigned to client "${selectedClient.name}"`);
      setQbDialogVisible(false);
    } catch (error) {
      addNotification('error', 'Failed to assign QuickBooks customer');
    }
  };

  const actionBodyTemplate = (rowData: Client) => {
    return (
      <div className="flex justify-content-center">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2" onClick={() => handleOpenDialog(rowData)} />
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mr-2" onClick={() => handleDeleteClient(rowData.id)} />
        <Button icon="pi pi-link" className="p-button-rounded p-button-info" tooltip="Assign QuickBooks Customer" onClick={() => handleOpenQbDialog(rowData)} />
      </div>
    );
  };

  const quickbooksCustomerIdTemplate = (rowData: Client) => {
    return rowData.quickbooksCustomerId ? (
      <span className="p-badge p-badge-success">{rowData.quickbooksCustomerId}</span>
    ) : (
      <span className="p-badge p-badge-warning">Not Assigned</span>
    );
  };

  const dialogFooter = (
    <>
      <Button label="Cancel" icon="pi pi-times" onClick={handleCloseDialog} className="p-button-text" />
      <Button label={editingClient ? 'Update' : 'Create'} icon="pi pi-check" onClick={handleSubmit} autoFocus />
    </>
  );

  const renderSkeleton = () => {
    return (
      <div className="grid">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="col-12 mb-3">
            <div className="flex justify-content-between align-items-center">
              <Skeleton width="60%" height="2rem" />
              <Skeleton width="20%" height="2rem" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">Manage Clients</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText type="search" onInput={(e) => dt.current?.filter(e.currentTarget.value, 'global', 'contains')} placeholder="Search..." />
      </span>
    </div>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <div className="card">
          <ConfirmDialog />
          {loading ? (
            renderSkeleton()
          ) : (
            <DataTable
              ref={dt}
              value={clients}
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 25]}
              responsiveLayout="scroll"
              emptyMessage="No clients found."
              header={header}
            >
              <Column field="name" header="Name" sortable filter filterPlaceholder="Search by name" />
              <Column field="contactPerson" header="Contact Person" sortable filter filterPlaceholder="Search by contact person" />
              <Column field="email" header="Email" sortable filter filterPlaceholder="Search by email" />
              <Column field="phone" header="Phone" sortable filter filterPlaceholder="Search by phone" />
              <Column field="quickbooksCustomerId" header="QuickBooks ID" body={quickbooksCustomerIdTemplate} sortable />
              <Column field="createdAt" header="Created At" body={(rowData) => formatDate(rowData.createdAt)} sortable />
              <Column body={actionBodyTemplate} headerStyle={{ width: '20%' }} bodyStyle={{ textAlign: 'center' }} />
            </DataTable>
          )}
          <div className="flex justify-content-end mt-3">
            <Button label="Add New Client" icon="pi pi-plus" className="p-button-success" onClick={() => handleOpenDialog()} />
          </div>

          <Dialog visible={openDialog} style={{ width: '450px' }} header={editingClient ? 'Edit Client' : 'Add New Client'} modal className="p-fluid" footer={dialogFooter} onHide={handleCloseDialog}>
            <div className="field">
              <label htmlFor="name">Name *</label>
              <InputText id="name" name="name" value={formData.name} onChange={handleInputChange} required autoFocus />
            </div>
            <div className="field">
              <label htmlFor="email">Email *</label>
              <InputText id="email" name="email" value={formData.email} onChange={handleInputChange} required />
            </div>
            <div className="field">
              <label htmlFor="contactPerson">Contact Person</label>
              <InputText id="contactPerson" name="contactPerson" value={formData.contactPerson} onChange={handleInputChange} />
            </div>
            <div className="field">
              <label htmlFor="phone">Phone</label>
              <InputText id="phone" name="phone" value={formData.phone} onChange={handleInputChange} />
            </div>
            <div className="field">
              <label htmlFor="address">Address</label>
              <InputTextarea id="address" name="address" value={formData.address} onChange={handleInputChange} rows={3} />
            </div>
          </Dialog>

          {/* QuickBooks Customer Assignment Dialog */}
          <Dialog 
            visible={qbDialogVisible} 
            style={{ width: '450px' }} 
            header="Assign QuickBooks Customer" 
            modal 
            className="p-fluid" 
            onHide={() => setQbDialogVisible(false)}
            footer={(
              <>
                <Button label="Cancel" icon="pi pi-times" onClick={() => setQbDialogVisible(false)} className="p-button-text" />
                <Button label="Assign" icon="pi pi-check" onClick={handleAssignQbCustomer} disabled={!selectedQbCustomer} />
              </>
            )}
          >
            {loadingQbCustomers ? (
              <Skeleton height="2rem" className="mb-2" />
            ) : (
              <>
                <div className="field">
                  <label htmlFor="qbCustomer">QuickBooks Customer</label>
                  <Dropdown
                    id="qbCustomer"
                    value={selectedQbCustomer}
                    options={qbCustomers}
                    onChange={(e) => setSelectedQbCustomer(e.value)}
                    optionLabel="name"
                    filter
                    filterBy="name"
                    placeholder="Select a QuickBooks Customer"
                    emptyMessage={qbCustomers.length === 0 ? "No QuickBooks customers found. Please connect to QuickBooks first." : "No customers found."}
                    emptyFilterMessage="No customers found."
                  />
                </div>
                <small className="p-error">
                  {qbCustomers.length === 0 && "No QuickBooks customers available. Make sure you've connected to QuickBooks and have customers in your account."}
                </small>
              </>
            )}
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default ClientManagement;
